<script>
// Module-level counter so each mounted instance gets unique SVG gradient IDs.
// Avoids conflicts when two instances of this component are on the same page.
let _count = 0;
</script>

<script setup>
import { computed, defineProps } from 'vue';

const props = defineProps({
    message: { type: String, default: '' },
    size:    { type: String, default: '80px' },
});

// Unique ID prefix for this instance's SVG gradient elements
const uid = `la3-${++_count}`;

// Scale factor: all geometry authored at 160 px; scale down to requested size
const scale = computed(() => parseFloat(props.size) / 160);

// Break each ring into N arc segments with stroke-width that varies by perspective:
// thick at the bottom (front of orbit, θ=90°), thin at the top (back, θ=270°).
// sin(θ) is 1 at bottom and -1 at top, giving a smooth 0→1 scale factor.
function makeRingSegs(rx, ry, cx, cy, maxSW, minSW, n = 36) {
    return Array.from({ length: n }, (_, i) => {
        const a1 = (i * 2 * Math.PI) / n;
        const a2 = ((i + 1) * 2 * Math.PI) / n;
        const mid = (a1 + a2) / 2;
        const sw = (minSW + (maxSW - minSW) * (Math.sin(mid) + 1) / 2).toFixed(2);
        const x1 = (cx + rx * Math.cos(a1)).toFixed(2);
        const y1 = (cy + ry * Math.sin(a1)).toFixed(2);
        const x2 = (cx + rx * Math.cos(a2)).toFixed(2);
        const y2 = (cy + ry * Math.sin(a2)).toFixed(2);
        return { d: `M ${x1} ${y1} A ${rx} ${ry} 0 0 1 ${x2} ${y2}`, sw };
    });
}

const outerSegs = makeRingSegs(64, 42, 80, 80, 12, 3);
const innerSegs = makeRingSegs(44, 28, 80, 74,  9, 2);
</script>

<template>
    <!--
        .la3-wrapper  — provides correct layout footprint (sized to `size` prop)
        .la3-scene    — 160 px reference container; scaled + mirrored via transform
    -->
    <div class="la3-wrapper" :style="{ width: props.size, height: props.size }">
        <div class="la3-scene">

            <!-- Static perspective ring overlay -->
            <svg
                class="la3-rings"
                width="160"
                height="160"
                viewBox="0 0 160 160"
                aria-hidden="true"
            >
                <defs>
                    <!--
                        gradientUnits="userSpaceOnUse" + coordinates in the
                        rotated group's local space → gradient goes from dim
                        at the back (top of orbit plane, y=57) to bright at
                        the front (bottom of orbit plane, y=103).
                    -->
                    <linearGradient
                        :id="`${uid}-outer`"
                        gradientUnits="userSpaceOnUse"
                        x1="80" y1="38" x2="80" y2="122"
                    >
                        <stop offset="0%"   stop-color="#cc2222" stop-opacity="0.15"/>
                        <stop offset="45%"  stop-color="#cc2222" stop-opacity="0.48"/>
                        <stop offset="100%" stop-color="#cc2222" stop-opacity="0.85"/>
                    </linearGradient>
                    <linearGradient
                        :id="`${uid}-inner`"
                        gradientUnits="userSpaceOnUse"
                        x1="80" y1="46" x2="80" y2="102"
                    >
                        <stop offset="0%"   stop-color="#cc2222" stop-opacity="0.12"/>
                        <stop offset="45%"  stop-color="#cc2222" stop-opacity="0.40"/>
                        <stop offset="100%" stop-color="#cc2222" stop-opacity="0.75"/>
                    </linearGradient>
                </defs>

                <!-- Rotate the ring plane -22° to match the logo's ring angle -->
                <g transform="rotate(-22 80 80)">
                    <!-- Outer ring: 36 arcs, stroke-width 3→12 thin(back)→thick(front) -->
                    <path v-for="(seg, i) in outerSegs" :key="`o${i}`"
                          :d="seg.d" fill="none"
                          :stroke="`url(#${uid}-outer)`"
                          :stroke-width="seg.sw"
                          stroke-linecap="round"/>
                    <!-- Inner ring: 36 arcs, stroke-width 2→9 -->
                    <path v-for="(seg, i) in innerSegs" :key="`r${i}`"
                          :d="seg.d" fill="none"
                          :stroke="`url(#${uid}-inner)`"
                          :stroke-width="seg.sw"
                          stroke-linecap="round"/>
                </g>
            </svg>

            <!-- 3. Orbit plane: zero-size div anchored to center, tilted -22° -->
            <div class="la3-orbit-plane">
                <!--
                    Dot animates along a 24-keyframe ellipse (rx=54, ry=20).
                    animation-direction: reverse corrects the orbit direction
                    after the scaleX(-1) mirror applied by .la3-scene.
                -->
                <div class="la3-dot"></div>
            </div>

        </div>
    </div>

    <p v-if="props.message" class="text-sm text-gray-700">{{ props.message }}</p>
</template>

<style scoped>
/* Outer layout shell — takes up exactly `size` × `size` in document flow */
.la3-wrapper {
    position: relative;
    display: inline-block;
    transform: rotate(45deg);
}

/*
    Inner scene — always 160 × 160 px (reference size).
    Centered in wrapper, then:
      scaleX(-1)   → mirrors the whole thing horizontally
      scale(scale) → shrinks/grows to match the size prop
    v-bind(scale) is Vue 3.2+ CSS variable binding (compiles to a CSS custom property).
*/
.la3-scene {
    position: absolute;
    width: 160px;
    height: 160px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scaleX(-1) scale(v-bind(scale));
}

.la3-rings {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    pointer-events: none;
}

/* Zero-size anchor at center of logo, rotated to match ring plane tilt */
.la3-orbit-plane {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    transform: rotate(-22deg);
    z-index: 3;
}

.la3-dot {
    position: absolute;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #d42020;
    box-shadow: 0 0 0 3px white, 0 0 7px 3px rgba(212, 32, 32, 0.4);
    top: -7.5px;
    left: -7.5px;
    animation: la3-orbit 1.4s linear infinite reverse;
}

/*
    Ellipse path: rx=54 px, ry=20 px (in orbit-plane coordinates).
    Start at θ=210° (the gap position matching the logo's static dot).
    scale: 0.50 (back/far) → 1.50 (front/close)  opacity: 0.50 → 1.00
*/
/*
  Keyframe percentages are non-uniform: derived from integrating
  1/speed(θ) where speed = 1 + 0.65·sin(θ). This makes the dot
  linger at the back (slow) and zip through the front (slingshot).
*/
@keyframes la3-orbit {
    0%    { transform: translate(-55px, -21px) scale(0.75); opacity: 0.62; }
    5.2%  { transform: translate(-45px, -30px) scale(0.65); opacity: 0.57; }
    11.8% { transform: translate(-32px, -36px) scale(0.57); opacity: 0.53; }
    19.7% { transform: translate(-17px, -41px) scale(0.52); opacity: 0.51; }
    28.6% { transform: translate(  0px, -42px) scale(0.50); opacity: 0.50; }
    37.5% { transform: translate( 17px, -41px) scale(0.52); opacity: 0.51; }
    45.4% { transform: translate( 32px, -36px) scale(0.57); opacity: 0.53; }
    52.0% { transform: translate( 45px, -30px) scale(0.65); opacity: 0.57; }
    57.2% { transform: translate( 55px, -21px) scale(0.75); opacity: 0.62; }
    61.4% { transform: translate( 62px, -11px) scale(0.87); opacity: 0.69; }
    64.9% { transform: translate( 64px,   0px) scale(1.00); opacity: 0.75; }
    67.8% { transform: translate( 62px,  11px) scale(1.13); opacity: 0.81; }
    70.3% { transform: translate( 55px,  21px) scale(1.25); opacity: 0.87; }
    72.6% { transform: translate( 45px,  30px) scale(1.35); opacity: 0.93; }
    74.7% { transform: translate( 32px,  36px) scale(1.43); opacity: 0.97; }
    76.7% { transform: translate( 17px,  41px) scale(1.48); opacity: 0.99; }
    78.6% { transform: translate(  0px,  42px) scale(1.50); opacity: 1.00; }
    80.5% { transform: translate(-17px,  41px) scale(1.48); opacity: 0.99; }
    82.5% { transform: translate(-32px,  36px) scale(1.43); opacity: 0.97; }
    84.6% { transform: translate(-45px,  30px) scale(1.35); opacity: 0.93; }
    86.9% { transform: translate(-55px,  21px) scale(1.25); opacity: 0.87; }
    89.4% { transform: translate(-62px,  11px) scale(1.13); opacity: 0.81; }
    92.3% { transform: translate(-64px,   0px) scale(1.00); opacity: 0.75; }
    95.8% { transform: translate(-62px, -11px) scale(0.87); opacity: 0.69; }
    100%  { transform: translate(-55px, -21px) scale(0.75); opacity: 0.62; }
}
</style>
